import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  contrastRatio,
  normalizeHexColor,
  resolveFontStack,
} from './design-options.js';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(cents, currency = 'EUR') {
  const amount = (Number(cents) || 0) / 100;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatEuro(cents) {
  return formatCurrency(cents, 'EUR');
}

export function formatDate(isoString, includeTime = true) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    return d.toLocaleDateString('de-DE', options);
  } catch {
    return String(isoString);
  }
}

export function formatInquiryHref(product, tenant) {
  const email = (tenant?.contactEmail || tenant?.contact_email || '').trim();
  if (!email) return '';
  const prodName = product?.name || 'Produkt';
  const sku = product?.sku || product?.id || '';
  const subject = encodeURIComponent(`Anfrage zu Produkt: ${prodName} (Art.-Nr. ${sku})`);
  const body = encodeURIComponent(
    `Guten Tag,\n\nich interessiere mich für das Produkt "${prodName}" (Artikel-Nr: ${sku}).\nBitte senden Sie mir ein unverbindliches Angebot zu.\n\nMit freundlichen Grüßen`
  );
  return `mailto:${email}?subject=${subject}&body=${body}`;
}

export function getContrastColor(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return '#ffffff';
  const cleanHex = hexColor.replace('#', '');
  if (cleanHex.length !== 6) return '#ffffff';
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (luminance > 0.6) return '#1a1c1c';
  // YIQ überschätzt helle Grün-, Orange- und Cyantöne (z. B. #2ecc71: weiße Schrift nur 2,1:1).
  // Weiß bleibt Standard, solange es wenigstens 3:1 erreicht (WCAG-Mindestwert für große Schrift und
  // UI-Elemente, im Designer als „fair“ bewertet); darunter wird die dunkle Schrift gewählt. Das ändert
  // nur Farben, bei denen Weiß ohnehin unlesbar wäre; bestehende Markenfarben bleiben unverändert.
  const whiteRatio = contrastRatio(`#${cleanHex}`, '#ffffff');
  return typeof whiteRatio === 'number' && whiteRatio < 3 ? '#1a1c1c' : '#ffffff';
}

// Hex wird normalisiert; Altwerte (z. B. benannte CSS-Farben aus früheren Seeds)
// bleiben unverändert, damit ein Re-Apply nie das bisherige Erscheinungsbild kippt.
function colorOrFallback(value, fallback) {
  const hex = normalizeHexColor(value);
  if (hex) return hex;
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function setBrandColors(root, primary, secondary) {
  root.style.setProperty('--primary', primary);
  root.style.setProperty('--primary-container', primary);
  root.style.setProperty('--primary-foreground', getContrastColor(primary));
  root.style.setProperty('--on-primary', getContrastColor(primary));
  root.style.setProperty('--secondary', secondary);
  root.style.setProperty('--secondary-foreground', getContrastColor(secondary));
  root.style.setProperty('--ring', `${primary}33`);
}

export function applyTenantTheme(theme) {
  if (typeof document === 'undefined' || !theme) return;
  const root = document.documentElement;

  const primary = theme.primaryColor || '#e3000b';
  const secondary = theme.secondaryColor || '#5f5e5f';
  const radius = theme.borderRadius || theme.radius || '0.375rem';
  const fontFamily = theme.fontFamily || theme.font_family;
  const fontHeading = theme.fontHeading || theme.font_heading;

  setBrandColors(root, primary, secondary);
  root.style.setProperty('--accent', theme.accent || secondary);
  root.style.setProperty('--radius', radius);

  if (fontFamily) {
    root.style.setProperty('--font-sans', fontFamily);
  }
  if (fontHeading) {
    root.style.setProperty('--font-heading', fontHeading);
  }
}

/**
 * Wendet NUR die per Design-Drawer änderbaren Marken-Variablen an (Farben und
 * Schriften) und räumt gesetzte Schrift-Overrides wieder ab, wenn der Wert
 * fehlt. Läuft bei Live-Vorschau und nach jedem Speichern; fasst bewusst weder
 * --accent noch --radius an (die gehören dem Theme-Preset).
 *
 * `fontFamily`/`fontHeading` sind Schrift-IDs (oder Altwerte); unbekannte Werte
 * werden ignoriert, so landet nie ein Freitext im style-Attribut.
 */
export function applyBrandOverrides({ primaryColor, secondaryColor, fontFamily, fontHeading } = {}) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  setBrandColors(
    root,
    colorOrFallback(primaryColor, DEFAULT_PRIMARY_COLOR),
    colorOrFallback(secondaryColor, DEFAULT_SECONDARY_COLOR),
  );

  const bodyStack = resolveFontStack(fontFamily);
  if (bodyStack) root.style.setProperty('--font-sans', bodyStack);
  else root.style.removeProperty('--font-sans');

  const headingStack = resolveFontStack(fontHeading);
  if (headingStack) root.style.setProperty('--font-heading', headingStack);
  else root.style.removeProperty('--font-heading');
}
